// Portable 2D raster surface.
//
// OffscreenCanvas is unavailable in Safari < 16.4 (and was absent entirely
// before that). This factory prefers OffscreenCanvas, falls back to a detached
// <canvas> on the main thread, and returns null only in a Worker on old Safari
// (where document is also unavailable) — callers handle that via rasterToBlob's
// fast-png branch.
//
// Both OffscreenCanvas and HTMLCanvasElement expose an identical
// getContext('2d') / createImageData / putImageData surface, so per-pixel fill
// loops written against one work unchanged against the other.

export function createRasterCanvas(width, height) {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height); // Chrome, Safari 16.4+
  }
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas'); // main-thread fallback (Safari < 16.4)
    c.width = width;
    c.height = height;
    return c;
  }
  return null; // Worker on old Safari — caller must pass imageData to rasterToBlob
}

// Produce a PNG Blob from any surface createRasterCanvas returns (including null,
// where it encodes the supplied ImageData with fast-png). `imageData` is only
// needed for the null/worker path but is cheap to always pass.
export async function rasterToBlob(canvasOrNull, { width, height, imageData } = {}) {
  if (canvasOrNull?.convertToBlob) return canvasOrNull.convertToBlob();          // OffscreenCanvas
  if (canvasOrNull?.toBlob) {                                                    // HTMLCanvasElement
    // toBlob yields null on encode failure — reject so the result is a clean Promise<Blob>.
    return new Promise((resolve, reject) => canvasOrNull.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob produced null'))),
    ));
  }
  // No canvas (old-Safari worker): encode the RGBA ImageData directly.
  if (!imageData?.data) {
    throw new TypeError('rasterToBlob: imageData {data,width,height} is required when no canvas is available');
  }
  const { encode } = await import('fast-png');
  const png = encode({
    width, height, data: imageData.data, depth: 8, channels: 4,
  });
  return new Blob([png], { type: 'image/png' });
}
